import { IconChevronLeft16, IconChevronRight16, UIHeading5 } from '@cian/ui-kit';
import { splitEvery } from 'ramda';
import * as React from 'react';

import { ArrowButton, Calendar, Cell, Month, Week } from '../../components/calendar';
import { DAYS_OF_WEEK, MONTHS, MONTHS_TO_DISPLAY } from '../../constants/calendar';
import { ICalendarDay, TCalendarDay } from '../../types/calendar';
import { calendarReducer } from './reducer';
import { ICalendarContainerProps } from './types';
import { prepareInitialState } from './utils';

export const CalendarContainer = ({ onChange, ...props }: ICalendarContainerProps) => {
  const [state, dispatch] = React.useReducer(calendarReducer, prepareInitialState(props));

  const visibleMonths = React.useMemo(() => {
    return state.calendar.slice(state.currentPage, state.currentPage + MONTHS_TO_DISPLAY);
  }, [state.calendar, state.currentPage]);

  const handlePrevClick = React.useCallback((e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();

    dispatch({ type: 'calendar/prev-page' });
  }, []);

  const handleNextClick = React.useCallback((e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();

    dispatch({ type: 'calendar/next-page' });
  }, []);

  const handleCellClick = React.useCallback(
    (day: ICalendarDay) => () =>
      dispatch({
        type: 'calendar/click-on-date',
        day,
      }),
    [],
  );

  const prevButton = state.isPrevPageAvailable && (
    <ArrowButton onClick={handlePrevClick}>
      <IconChevronLeft16 color="icon-main-default" />
    </ArrowButton>
  );
  const nextButton = state.isNextPageAvailable && (
    <ArrowButton onClick={handleNextClick}>
      <IconChevronRight16 color="icon-main-default" />
    </ArrowButton>
  );

  const months = visibleMonths.map((month, monthIndex) => {
    const { number, year, days } = month;

    return (
      <Month
        key={monthIndex}
        header={
          <UIHeading5>
            {MONTHS[number]}&nbsp;{year}
          </UIHeading5>
        }
      >
        <Week>
          {DAYS_OF_WEEK.map((text, index) => (
            <Cell key={index} type="day_of_week" content={text} />
          ))}
        </Week>
        {splitEvery(7, days).map((week: TCalendarDay[], index) => (
          <Week key={`week-${index}`}>
            {week.map((day, dayIndex) => {
              if (day) {
                let onClick: VoidFunction | undefined = handleCellClick(day);

                if (day.type === 'disabled') {
                  onClick = undefined;
                }

                return <Cell key={dayIndex} type={day.type} content={day.number} onClick={onClick} />;
              }

              return <Cell key={dayIndex} type="none" />;
            })}
          </Week>
        ))}
      </Month>
    );
  });

  React.useLayoutEffect(() => {
    if (state.from && state.to && state.updated) {
      dispatch({ type: 'calendar/reset-updated' });

      onChange(state.from, state.to);
    }
  }, [onChange, dispatch, state.from, state.to, state.updated]);

  return <Calendar months={months} prevButton={prevButton} nextButton={nextButton} />;
};
